import { Suspense, type ComponentType, type ReactNode } from 'react';
import { Skeleton } from '@reflex/ui/components/ui/skeleton';
import type { AttachmentSummaryProps, PluginAttachmentPickerProps } from '@reflex/plugin-api';
import type { ResolvedAttachment } from '@/lib/plugin-registry';
import type { AttachmentPickerProviders } from '@/hooks/config/useAttachmentPickerProviders';

/**
 * Shared pieces for the two surfaces that render a plugin attachment as a chip:
 * the Advanced bar's `AttachmentChip` and the launch composer's applied-context
 * row. Both resolve the same optional summary component and host the same
 * picker, so that logic lives here once instead of drifting between them. The
 * matching `summaryIncludesIcon` rule lives in `attachment-chip-utils` because
 * fast refresh requires this file to export components only.
 */

export interface AttachmentSummaryLabelProps {
  definition: ResolvedAttachment;
  /** The attachment's stored config, or `null` when it has none. */
  config: unknown;
  /**
   * Passed through to the summary so it can offer a narrow in-place edit (the
   * repo chip's branch field). Omit on surfaces that render the summary purely
   * as a label — the summary contract requires it to stay read-only then.
   */
  onChange?: (config: unknown) => void;
  /** Rendered when the plugin ships no summary component. */
  fallback: ReactNode;
}

/**
 * Renders an attachment's glanceable summary, or `fallback` when the plugin
 * ships none. Lazy summaries are Suspense-wrapped so a chip never suspends its
 * host row while the module loads.
 */
export function AttachmentSummaryLabel({
  definition,
  config,
  onChange,
  fallback,
}: AttachmentSummaryLabelProps) {
  const Summary = definition.summaryComponent as ComponentType<AttachmentSummaryProps> | undefined;
  if (!Summary) return <>{fallback}</>;
  return (
    <Suspense fallback={<span className="text-muted-foreground">…</span>}>
      <Summary value={config ?? null} onChange={onChange} />
    </Suspense>
  );
}

export interface AttachmentPickerPanelProps extends AttachmentPickerProviders {
  definition: ResolvedAttachment;
  /** The attachment's stored config, or `null`/`undefined` when unconfigured. */
  config: unknown;
  /** Writes a fresh config, or `null` to clear the attachment entirely. */
  onChange: (config: unknown | null) => void;
}

/**
 * Hosts a plugin's own picker component. Provider state is injected rather than
 * read from a hook here so the chips stay renderable without app context.
 */
export function AttachmentPickerPanel({
  definition,
  config,
  onChange,
  providers,
  connectProvider,
  connectingProvider,
}: AttachmentPickerPanelProps) {
  const Picker = definition.pickerComponent as ComponentType<PluginAttachmentPickerProps>;
  return (
    <Suspense fallback={<Skeleton className="h-5" />}>
      <Picker
        value={config ?? null}
        onChange={onChange}
        providers={providers}
        connectProvider={connectProvider}
        connectingProvider={connectingProvider}
      />
    </Suspense>
  );
}
